import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  checkVoicesFeed,
  closeVoicesCampaign,
  createVoicesCampaign,
  createVoicesFeed,
  deleteVoicesCampaign,
  deleteVoicesFeed,
  getVoicesCampaign,
  getVoicesCampaigns,
  importVoicesCampaigns,
  updateVoicesCampaign,
  updateVoicesFeed,
} from "../Api/voicesCampaigns";
import {
  VoicesCampaign,
  VoicesCampaignPayload,
  VoicesFeedCheck,
  VoicesFeedPayload,
} from "../Models/voicesCampaigns";

export const useVoicesCampaigns = () => {
  return useQuery<VoicesCampaign[], Error>({
    queryKey: ["voicesCampaigns"],
    queryFn: () => getVoicesCampaigns(),
  });
};

export const useVoicesCampaign = (campaignId: number) => {
  return useQuery<VoicesCampaign, Error>({
    queryKey: ["voicesCampaign", campaignId],
    queryFn: () => getVoicesCampaign(campaignId),
    enabled: !!campaignId,
    retry: false,
  });
};

export const useVoicesFeedCheck = (campaignId: number, feedId: number | null) => {
  return useQuery<VoicesFeedCheck, Error>({
    queryKey: ["voicesFeedCheck", campaignId, feedId],
    queryFn: () => checkVoicesFeed(campaignId, feedId as number),
    enabled: feedId !== null,
    gcTime: 0,
    retry: false,
  });
};

const useCampaignMutation = <TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesCampaigns"] });
      queryClient.invalidateQueries({ queryKey: ["voicesCampaign"] });
    },
  });
};

export const useSaveVoicesCampaign = () =>
  useCampaignMutation(
    (variables: { campaignId: number | null; data: VoicesCampaignPayload }) =>
      variables.campaignId === null
        ? createVoicesCampaign(variables.data)
        : updateVoicesCampaign(variables.campaignId, variables.data),
  );

export const useCloseVoicesCampaign = () =>
  useCampaignMutation((campaignId: number) => closeVoicesCampaign(campaignId));

export const useDeleteVoicesCampaign = () =>
  useCampaignMutation((campaignId: number) => deleteVoicesCampaign(campaignId));

export const useSaveVoicesFeed = () =>
  useCampaignMutation(
    (variables: {
      campaignId: number;
      feedId: number | null;
      data: Partial<VoicesFeedPayload>;
    }) =>
      variables.feedId === null
        ? createVoicesFeed(variables.campaignId, variables.data as VoicesFeedPayload)
        : updateVoicesFeed(variables.campaignId, variables.feedId, variables.data),
  );

export const useDeleteVoicesFeed = () =>
  useCampaignMutation((variables: { campaignId: number; feedId: number }) =>
    deleteVoicesFeed(variables.campaignId, variables.feedId),
  );

export const usePreviewVoicesCampaignImport = () =>
  useMutation({
    mutationFn: (document: unknown) => importVoicesCampaigns(document, false),
  });

export const useImportVoicesCampaigns = () =>
  useCampaignMutation((document: unknown) => importVoicesCampaigns(document, true));
